import React, { useState } from 'react'
import { makeStyles, IconButton } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import CloseIcon from '@material-ui/icons/Close'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import type { PainterMediaItem } from './PainterScheduleView/types'

export type { PainterMediaItem }

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ViewMediaPanelProps {
  title?: string
  items?: PainterMediaItem[]
  onClose?: () => void
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles<Theme>(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#fff',
    overflow: 'hidden'
  },

  // ── Header ──────────────────────────────────────────────────────────────────
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 16px 17px',
    borderBottom: '1px solid #e5e7eb',
    flexShrink: 0,
    backgroundColor: '#fff'
  },
  headerBack: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    color: '#252e3d'
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 500,
    color: '#252e3d',
    lineHeight: 1.4,
    flex: 1,
    textAlign: 'left'
  },
  headerTitleCentered: {
    textAlign: 'center'
  },
  headerClose: {
    padding: 0,
    color: '#738198',
    flexShrink: 0
  },
  headerSpacer: {
    width: 20,
    flexShrink: 0
  },

  // ── Scroll body ─────────────────────────────────────────────────────────────
  body: {
    flex: 1,
    overflowY: 'auto',
    backgroundColor: '#fff',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    minHeight: 0
  },

  // ── Media card ──────────────────────────────────────────────────────────────
  card: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 4,
    cursor: 'pointer',
    flex: 1,
    minHeight: 0
  },
  cardImage: {
    position: 'relative',
    width: '100%',
    flex: 1,
    minHeight: 0,
    border: '1px solid #d9dee8',
    borderRadius: '4px 4px 0 0',
    overflow: 'hidden',
    backgroundColor: '#f7f8fa'
  },
  cardImg: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: '4px 4px 0 0'
  },
  cardImageOnly: {
    borderRadius: 4
  },
  pageBadge: {
    position: 'absolute',
    bottom: 16,
    left: 16,
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: 'rgba(46,59,81,0.5)',
    backdropFilter: 'blur(1.5px)',
    border: '1px solid rgba(146,159,181,0.5)',
    borderRadius: 4,
    padding: '1px 6px',
    height: 20
  },
  pageBadgeText: {
    fontSize: 12,
    fontWeight: 500,
    color: '#fff',
    lineHeight: 1.4,
    whiteSpace: 'nowrap'
  },
  cardCaption: {
    borderBottom: '1px solid #d9dee8',
    borderLeft: '1px solid #d9dee8',
    borderRight: '1px solid #d9dee8',
    borderRadius: '0 0 4px 4px',
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  cardCaptionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#252e3d',
    lineHeight: 1.4
  },
  cardCaptionDesc: {
    fontSize: 14,
    fontWeight: 400,
    color: '#738198',
    lineHeight: 1.4
  },

  // ── Document detail view ─────────────────────────────────────────────────────
  docDescription: {
    fontSize: 14,
    fontWeight: 400,
    color: '#738198',
    lineHeight: 1.4,
    padding: '0 3px',
    marginBottom: 2
  },
  docPage: {
    position: 'relative',
    width: '100%',
    border: '1px solid #d9dee8',
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: '#f7f8fa'
  },
  docPageImg: {
    display: 'block',
    width: '100%',
    objectFit: 'cover',
    borderRadius: 4
  },
  docPageBadge: {
    position: 'absolute',
    top: 16,
    left: 16,
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: 'rgba(46,59,81,0.5)',
    backdropFilter: 'blur(1.5px)',
    border: '1px solid rgba(146,159,181,0.5)',
    borderRadius: 4,
    padding: '1px 6px',
    height: 20
  },
  docPageBadgeText: {
    fontSize: 12,
    fontWeight: 500,
    color: '#fff',
    lineHeight: 1.4,
    whiteSpace: 'nowrap'
  }
}))

// ─── Component ────────────────────────────────────────────────────────────────

export default function ViewMediaPanel({ title, items = [], onClose }: ViewMediaPanelProps) {
  const classes = useStyles()
  const [selectedDoc, setSelectedDoc] = useState<PainterMediaItem | null>(null)

  if (selectedDoc) {
    return (
      <DocumentDetailView classes={classes} item={selectedDoc} onBack={() => setSelectedDoc(null)} onClose={onClose} />
    )
  }

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <span className={classes.headerTitle}>{title ?? 'Media'}</span>
        <IconButton className={classes.headerClose} size="small" onClick={onClose}>
          <CloseIcon style={{ fontSize: 20 }} />
        </IconButton>
      </div>

      {/* Gallery list */}
      <div className={classes.body}>
        {items.map((item) => (
          <MediaCard
            key={item.id}
            classes={classes}
            item={item}
            onClick={item.type === 'document' ? () => setSelectedDoc(item) : undefined}
          />
        ))}
      </div>
    </div>
  )
}

// ─── MediaCard ────────────────────────────────────────────────────────────────

interface MediaCardProps {
  classes: ReturnType<typeof useStyles>
  item: PainterMediaItem
  onClick?: () => void
}

function MediaCard({ classes, item, onClick }: MediaCardProps) {
  const pageCount = item.pageUrls ? item.pageUrls.length : item.type === 'document' ? 1 : 0
  const hasCaption = !!(item.title || item.description)

  return (
    <div
      className={classes.card}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className={classes.cardImage} style={hasCaption ? undefined : { borderRadius: 4 }}>
        <img
          src={item.url}
          alt={item.title}
          className={classes.cardImg}
          style={hasCaption ? undefined : { borderRadius: 4 }}
        />
        {item.type === 'document' && pageCount > 1 && (
          <div className={classes.pageBadge}>
            <span className={classes.pageBadgeText}>Page 1 of {pageCount}</span>
          </div>
        )}
      </div>
      {hasCaption && (
        <div className={classes.cardCaption}>
          {item.title && <span className={classes.cardCaptionTitle}>{item.title}</span>}
          {item.description && <span className={classes.cardCaptionDesc}>{item.description}</span>}
        </div>
      )}
    </div>
  )
}

// ─── DocumentDetailView ───────────────────────────────────────────────────────

interface DocumentDetailViewProps {
  classes: ReturnType<typeof useStyles>
  item: PainterMediaItem
  onBack: () => void
  onClose?: () => void
}

function DocumentDetailView({ classes, item, onBack, onClose }: DocumentDetailViewProps) {
  const pages = item.pageUrls ?? [item.url]
  const total = pages.length

  return (
    <div className={classes.root}>
      {/* Header: back + centered title + close */}
      <div className={classes.header}>
        <IconButton
          className={classes.headerBack}
          size="small"
          onClick={onBack}
          style={{ padding: 0, color: '#252e3d' }}
        >
          <ChevronLeftIcon style={{ fontSize: 22 }} />
        </IconButton>
        <span className={`${classes.headerTitle} ${classes.headerTitleCentered}`}>{item.title}</span>
        <IconButton className={classes.headerClose} size="small" onClick={onClose}>
          <CloseIcon style={{ fontSize: 20 }} />
        </IconButton>
      </div>

      {/* Document pages */}
      <div className={classes.body}>
        {item.description && <span className={classes.docDescription}>{item.description}</span>}
        {pages.map((pageUrl, idx) => (
          <div key={idx} className={classes.docPage}>
            <img src={pageUrl} alt={`${item.title} page ${idx + 1}`} className={classes.docPageImg} />
            <div className={classes.docPageBadge}>
              <span className={classes.docPageBadgeText}>
                Page {idx + 1} of {total}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
